import type {
  AxisTextProps,
  ChartContainerProps,
  ChartSkeletonContainerProps,
  CustomizedDotProps,
  SkeletonBarProps,
  SkeletonProps,
  TooltipWithFallbackProps,
  XAxisTickProps,
  YAxisTickProps,
} from './types'

import React from 'react'
import { Text } from 'recharts'

import { BodySubtext, variantStyles } from '@/components/atomic/text'
import { formatYield } from '@/utils/presentation/bonds'
import { formatPrice } from '@/utils/presentation/formatting'
import { cn } from '@/utils/shadcn/utils'

import { CHART_OFFSETS } from './constants'
import styles from './line-chart.module.css'

export const ChartContainer = React.forwardRef<
  HTMLDivElement,
  ChartContainerProps
>(({ children, className }, ref) => {
  return (
    <div ref={ref} className={cn('relative w-full h-full', className)}>
      {children}
    </div>
  )
})
ChartContainer.displayName = 'ChartContainer'

function ChartSkeletonContainer({
  children,
  isFetching,
}: ChartSkeletonContainerProps) {
  return (
    <div
      className={`absolute left-3 z-10 ${styles['skeleton-container']} ${isFetching ? styles.visible : ''}`}
    >
      {children}
    </div>
  )
}

function SkeletonBar({ y, className }: SkeletonBarProps) {
  return (
    <>
      <div
        className={cn(
          'absolute h-5 w-[70px]',
          styles['skeleton-white'],
          '-translate-y-1/2',
          className,
        )}
        style={{ top: `${y}px` }}
      />
      <div
        className="absolute h-5 w-9 bg-moment-teal-200 dark:bg-moment-teal-1000 rounded-md animate-pulse -translate-y-1/2"
        style={{ top: `${y}px` }}
      />
    </>
  )
}

export function EmptyState() {
  return (
    <Text
      style={{ transform: `translate(50%, 50%)` }}
      x={0}
      y={0}
      textAnchor="middle"
      verticalAnchor="middle"
      className={cn(variantStyles.bodySmall)}
    ></Text> // Temporary fix for content flash on load
  )
}

function ChartAxis({
  x,
  y,
  payload,
  className,
  textAnchor = 'middle',
  xOffset = 0,
  dy = 16,
}: AxisTextProps) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={xOffset}
        y={0}
        dy={dy}
        fontSize={12}
        className={className}
        fill="var(--secondary)"
        textAnchor={textAnchor}
        opacity={1}
      >
        {payload.value}
      </text>
    </g>
  )
}

export function CustomXAxisTick({
  x,
  y,
  payload,
  isTransitioning,
  formatter,
}: XAxisTickProps) {
  const textAnchor = 'middle' as AxisTextProps['textAnchor']

  const displayValue = formatter ? formatter(payload.value) : payload.value

  return (
    <ChartAxis
      x={x}
      y={y}
      payload={{ ...payload, value: displayValue }}
      className={isTransitioning ? styles['axis-text-fade'] : ''}
      textAnchor={textAnchor}
      xOffset={1}
    />
  )
}

export function CustomYAxisTick({
  x,
  y,
  payload,
  displayMode,
  digits,
}: YAxisTickProps) {
  const displayValue =
    displayMode === 'yield'
      ? formatYield(payload.value, false, digits)
      : formatPrice(payload.value, {}, digits)

  const width = Math.max(displayValue.length * 11.5, 40)

  return (
    <g transform={`translate(${x},${y})`}>
      <defs>
        <linearGradient id="fadeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop
            offset="70%"
            stopColor="var(--widget-background)"
            stopOpacity="1"
          />
          <stop
            offset="100%"
            stopColor="var(--widget-background)"
            stopOpacity="0"
          />
        </linearGradient>
      </defs>
      <rect x={0} y={-10} width={width} height={20} fill="url(#fadeGradient)" />
      <ChartAxis
        x={0}
        y={0}
        payload={{ value: displayValue }}
        textAnchor="start"
        dy={4}
      />
    </g>
  )
}

export function CustomizedDot({ cx, cy, index, data }: CustomizedDotProps) {
  // Early return if required props are missing
  if (
    typeof cx === 'undefined' ||
    typeof cy === 'undefined' ||
    typeof index === 'undefined' ||
    !data?.length
  ) {
    return null
  }

  if (index === data.length - 1) {
    return (
      <g className={styles['dot-visible']} style={{ opacity: 0 }}>
        <defs>
          <filter
            id="blur"
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
            filterUnits="userSpaceOnUse"
          >
            <feGaussianBlur stdDeviation="0.5" />
          </filter>
        </defs>
        <circle
          cx={cx}
          cy={cy}
          r={0}
          fill="none"
          stroke="var(--line-chart-stroke)"
          strokeWidth={1}
          opacity={0}
          filter="url(#blur)"
        >
          <animate
            attributeName="r"
            from="1"
            to="11"
            dur="2.2s"
            begin="2.5s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            from="1"
            to="0"
            dur="2.2s"
            begin="2.5s"
            repeatCount="indefinite"
          />
        </circle>
        <circle cx={cx} cy={cy} r={4} fill="var(--line-chart-stroke)" />
      </g>
    )
  }
}

export function TooltipWithFallback({
  active,
  payload,
  label,
  tooltipFormatter,
  series,
}: TooltipWithFallbackProps) {
  if (active && payload?.length) {
    const dataPoint = payload[0].payload
    if (tooltipFormatter) {
      return tooltipFormatter(dataPoint, series)
    }

    // Fallback to default tooltip:
    return (
      <div className="bg-background p-2 shadow-md rounded-md">
        <BodySubtext className="text-secondary">{label}</BodySubtext>
        <BodySubtext>{`Value: ${dataPoint.y}`}</BodySubtext>
      </div>
    )
  }
  return null
}

export function Skeleton({ isFetching, containerRef }: SkeletonProps) {
  const getSkeletonOffsets = () => {
    const containerHeight = containerRef.current?.clientHeight ?? 0

    return CHART_OFFSETS.getHorizontalOffsets(containerHeight)
  }

  return (
    <ChartSkeletonContainer isFetching={isFetching}>
      {getSkeletonOffsets().map((y, index) => (
        <SkeletonBar key={index} y={y} />
      ))}
    </ChartSkeletonContainer>
  )
}
